import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/admin.css';
import { authFetch } from '../services/auth';
import { useMe } from '../store/useMe';

const ALL_ROLES = ['Admin', 'Lab', 'Accountant'];

interface AdminUser {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  position?: string | null;
  isActive: boolean;
  roles: string[];
  createdAt: string;
}

const emptyForm = { email: '', password: '', firstName: '', lastName: '', roles: ['Lab'] as string[] };
const emptyProfileForm = { firstName: '', lastName: '', position: '' };

export const AdminUsersPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { me } = useMe();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, string[]>>({});
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [profileUser, setProfileUser] = useState<AdminUser | null>(null);
  const [profileForm, setProfileForm] = useState(emptyProfileForm);
  const [deleteUser, setDeleteUser] = useState<AdminUser | null>(null);

  const roleLabel = (r: string) =>
    r === 'Admin' ? t('admin:roleAdmin') : r === 'Lab' ? t('admin:roleLab') : r === 'Accountant' ? t('admin:roleAccountant') : r;

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/admin/users');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsers(data.users || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const toggleRole = (roles: string[], role: string) =>
    roles.includes(role) ? roles.filter((r) => r !== role) : [...roles, role];

  const startEdit = (u: AdminUser) => setEditing((prev) => ({ ...prev, [u.id]: [...u.roles] }));
  const cancelEdit = (id: string) =>
    setEditing((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  const saveRoles = async (user: AdminUser) => {
    const roles = editing[user.id];
    setBusy(true);
    try {
      const res = await authFetch(`/api/admin/users/${user.id}/roles`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roles }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, roles } : u)));
      cancelEdit(user.id);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const openCreate = () => {
    setForm(emptyForm);
    setShowCreate(true);
  };

  const createUser = async () => {
    if (!form.email.trim() || !form.password.trim()) {
      alert(t('admin:usersEmailPasswordRequired'));
      return;
    }
    setBusy(true);
    try {
      const res = await authFetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      setShowCreate(false);
      await fetchUsers();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const fullName = (u: AdminUser) => [u.firstName, u.lastName].filter(Boolean).join(' ');

  const openProfileEdit = (u: AdminUser) => {
    setProfileForm({ firstName: u.firstName || '', lastName: u.lastName || '', position: u.position || '' });
    setProfileUser(u);
  };

  const saveProfile = async () => {
    if (!profileUser) return;
    setBusy(true);
    try {
      const res = await authFetch(`/api/admin/users/${profileUser.id}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileForm),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      setUsers((prev) =>
        prev.map((u) =>
          u.id === profileUser.id
            ? { ...u, firstName: profileForm.firstName || null, lastName: profileForm.lastName || null, position: profileForm.position || null }
            : u,
        ),
      );
      setProfileUser(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteUser) return;
    setBusy(true);
    try {
      const res = await authFetch(`/api/admin/users/${deleteUser.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      setUsers((prev) => prev.filter((u) => u.id !== deleteUser.id));
      setDeleteUser(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  if (me && !me.isAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <h1>{t('admin:usersTitle')}</h1>
        </div>
        <p className="empty-activity">{t('admin:usersForbidden')}</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1>{t('admin:usersTitle')}</h1>
          <p className="admin-subtitle">{t('admin:usersSubtitle')}</p>
        </div>
        <button type="button" className="btn-add-order" onClick={openCreate}>
          + {t('admin:addUser')}
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="admin-summary">
        <span>
          {t('admin:usersFound')} <strong>{users.length}</strong>
        </span>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin:formEmail')}</th>
              <th>{t('admin:usersColName')}</th>
              <th>{t('admin:rolesLabel')}</th>
              <th>{t('admin:status')}</th>
              <th className="col-actions">{t('admin:actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && !loading ? (
              <tr>
                <td colSpan={5} className="no-data">
                  {t('admin:usersNone')}
                </td>
              </tr>
            ) : (
              users.map((u) => {
                const isEditing = editing[u.id] !== undefined;
                const roles = isEditing ? editing[u.id] : u.roles;
                return (
                  <tr key={u.id}>
                    <td>
                      <strong>{u.email}</strong>
                    </td>
                    <td>{fullName(u) || <span className="detail-muted">—</span>}</td>
                    <td>
                      {isEditing ? (
                        <div className="role-toggles">
                          {ALL_ROLES.map((r) => {
                            const on = roles.includes(r);
                            return (
                              <button
                                key={r}
                                type="button"
                                className={`role-toggle role-${r.toLowerCase()}${on ? ' on' : ''}`}
                                onClick={() => setEditing((prev) => ({ ...prev, [u.id]: toggleRole(roles, r) }))}
                              >
                                {roleLabel(r)}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="role-chips">
                          {u.roles.length ? (
                            u.roles.map((r) => (
                              <span key={r} className={`role-chip role-${r.toLowerCase()}`}>
                                {roleLabel(r)}
                              </span>
                            ))
                          ) : (
                            <span className="detail-muted">—</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${u.isActive ? 'status-completed' : 'status-cancelled'}`}>
                        {u.isActive ? t('admin:active') : t('admin:inactive')}
                      </span>
                    </td>
                    <td className="col-actions">
                      {isEditing ? (
                        <div className="table-actions">
                          <button className="btn-row btn-row-primary" disabled={busy} onClick={() => saveRoles(u)}>
                            {t('admin:save')}
                          </button>
                          <button className="btn-row" onClick={() => cancelEdit(u.id)}>
                            {t('admin:cancel')}
                          </button>
                        </div>
                      ) : (
                        <div className="table-actions">
                          <button className="btn-row" onClick={() => openProfileEdit(u)}>
                            {t('admin:usersEdit')}
                          </button>
                          <button className="btn-row" onClick={() => startEdit(u)}>
                            {t('admin:editRoles')}
                          </button>
                          {me?.userId !== u.id && (
                            <button className="btn-row btn-row-danger" onClick={() => setDeleteUser(u)}>
                              {t('admin:delete')}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="modal-overlay" onClick={() => !busy && setShowCreate(false)}>
          <div className="modal create-order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admin:addUser')}</h2>
              <button className="modal-close" onClick={() => setShowCreate(false)}>
                ×
              </button>
            </div>
            <div className="confirm-body create-order-body">
              <div className="co-row">
                <div className="form-group">
                  <label>{t('admin:formEmail')} *</label>
                  <input
                    className="search-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin:formPassword')} *</label>
                  <input
                    className="search-input"
                    type="text"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </div>
              </div>
              <div className="co-row">
                <div className="form-group">
                  <label>{t('admin:formFirstName')}</label>
                  <input
                    className="search-input"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin:formLastName')}</label>
                  <input
                    className="search-input"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('admin:rolesLabel')}</label>
                <div className="role-toggles">
                  {ALL_ROLES.map((r) => {
                    const on = form.roles.includes(r);
                    return (
                      <button
                        key={r}
                        type="button"
                        className={`role-toggle role-${r.toLowerCase()}${on ? ' on' : ''}`}
                        onClick={() => setForm({ ...form, roles: toggleRole(form.roles, r) })}
                      >
                        {roleLabel(r)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="confirm-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)} disabled={busy}>
                {t('admin:cancel')}
              </button>
              <button className="btn btn-primary" onClick={createUser} disabled={busy}>
                {busy ? t('admin:saving') : t('admin:createUser')}
              </button>
            </div>
          </div>
        </div>
      )}

      {profileUser && (
        <div className="modal-overlay" onClick={() => !busy && setProfileUser(null)}>
          <div className="modal create-order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admin:usersEditTitle', { email: profileUser.email })}</h2>
              <button className="modal-close" onClick={() => setProfileUser(null)}>
                ×
              </button>
            </div>
            <div className="confirm-body create-order-body">
              <div className="co-row">
                <div className="form-group">
                  <label>{t('admin:formFirstName')}</label>
                  <input
                    className="search-input"
                    value={profileForm.firstName}
                    onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin:formLastName')}</label>
                  <input
                    className="search-input"
                    value={profileForm.lastName}
                    onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('admin:profilePosition')}</label>
                <input
                  className="search-input"
                  placeholder={t('admin:profilePositionPlaceholder')}
                  value={profileForm.position}
                  onChange={(e) => setProfileForm({ ...profileForm, position: e.target.value })}
                />
              </div>
            </div>
            <div className="confirm-footer">
              <button className="btn btn-secondary" onClick={() => setProfileUser(null)} disabled={busy}>
                {t('admin:cancel')}
              </button>
              <button className="btn btn-primary" onClick={saveProfile} disabled={busy}>
                {busy ? t('admin:saving') : t('admin:save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteUser && (
        <div className="modal-overlay" onClick={() => !busy && setDeleteUser(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admin:usersDeleteTitle')}</h2>
              <button className="modal-close" onClick={() => setDeleteUser(null)}>
                ×
              </button>
            </div>
            <div className="confirm-body">
              <p>{t('admin:usersDeleteConfirm', { email: deleteUser.email })}</p>
            </div>
            <div className="confirm-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteUser(null)} disabled={busy}>
                {t('admin:cancel')}
              </button>
              <button className="btn btn-danger" onClick={confirmDelete} disabled={busy}>
                {busy ? t('admin:saving') : t('admin:delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
